import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  settingsService,
  Settings,
  contactService,
} from "../services/firebase";
import { emailService } from "../services/emailService";
import { cacheService } from "../services/cacheService";
import { cookieService } from "../services/cookieService";

// import SocialIcons from "../components/SocialIcons";
// import Logo from "../assets/image/Logo";

import "./Contact.css";
import PrimaryButton from "components/PrimaryButton";

const Contact: React.FC = () => {
  const { t, i18n } = useTranslation();

  const [settings, setSettings] = useState<Settings>({
    siteName: "",
    siteDescription: "",
    contactEmail: "",
    contactPhone: "",
    socialMedia: {},
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [consentGiven, setConsentGiven] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    let isMounted = true; // למניעת memory leaks

    const fetchSettings = async () => {
      try {
        // נסה קודם מהמטמון
        const cacheKey = "settings";
        const cached = cacheService.get<Settings>(cacheKey);
        if (cached && isMounted) {
          setSettings(cached);
          setLoading(false);
          return; // אל תמשיך אם יש במטמון
        }

        // שלוף מהשרת רק אם אין במטמון
        if (isMounted) {
          const data = await settingsService.get();
          if (isMounted) {
            setSettings(data);
            cacheService.set(cacheKey, data, 10 * 60 * 1000); // 10 דקות
          }
        }
      } catch (error) {
        if (isMounted) {
                  }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSettings();

    // בדיקת עוגיות
    if (isMounted) {
      // שמירת זמן ביקור
      cookieService.setLastVisit();

      // שמירת העדפות שפה
      const currentLanguage = t("language") || "he";
      cookieService.setLanguage(currentLanguage);

      // שמירת העדפות נגישות (ברירת מחדל)
      const accessibilityPrefs = {
        fontSize: "medium" as const,
        highContrast: false,
        reduceMotion: false,
      };
      cookieService.setAccessibilityPreferences(accessibilityPrefs);
    }

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [t]);

  // פונקציית ולדציה למייל
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // פונקציה לטיפול בשינוי מייל
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    // ניקוי הודעת שגיאה כשהמשתמש מתחיל להקליד
    if (emailError) {
      setEmailError("");
    }
  };

  // פונקציה לטיפול ביציאה מהאינפוט
  const handleEmailBlur = () => {
    if (email && !validateEmail(email)) {
      setEmailError("כתובת מייל לא תקינה");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name || !message || !consentGiven || !validateEmail(email))
      return;

        
    setSubmitting(true);
    try {
      // שמירת ההודעה ב-Firebase
            await contactService.create({
        name,
        email,
        message,
      });
      
      // שליחת מייל אישור אוטומטי ללקוח (שפה וכיוון לפי שפת האתר)
      const lang = i18n.language === "ru" ? "ru" : "he";
      const emailStrings = {
        subject: t("emails.contactAutoReply.subject"),
        title: t("emails.contactAutoReply.title"),
        greeting: t("emails.contactAutoReply.greeting"),
        body: t("emails.contactAutoReply.body"),
        yourMessage: t("emails.contactAutoReply.yourMessage"),
        regards: t("emails.contactAutoReply.regards"),
        teamLabel: t("emails.contactAutoReply.teamLabel"),
        footer: t("emails.contactAutoReply.footer"),
        defaultName: t("emails.contactAutoReply.defaultName"),
      };
            await emailService.sendAutoReply(
        email,
        settings.siteName || "האתר",
        name,
        message,
        emailStrings,
        lang
      );
      
      setEmail("");
      setName("");
      setMessage("");
      setConsentGiven(false);
            setShowThankYou(true);
    } catch (error) {
                  alert(t("errorMessage"));
    } finally {
      setSubmitting(false);
    }
  };

  // קומפוננטה פנימית להודעת תודה
  const ThankYouMessage = () => (
    <div className="thank-you-container">
      <h2 className="thank-you-title-contact">{t("thankYouTitle")}!</h2>
      <p className="thank-you-message-contact">{t("thankYouMessage")}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="contact-page">
        <div className="loading">{t("loading")}</div>
      </div>
    );
  }

  if (showThankYou) {
    return (
      <div
        className="contact-page"
        id="contact"
        dir={i18n.language === "he" ? "rtl" : "ltr"}
        data-language={i18n.language}
        lang={i18n.language}
      >
        <div className="brown-line" />
        <main className="contact-main">
          <ThankYouMessage />
        </main>
      </div>
    );
  }

  return (
    <div
      className="contact-page"
      id="contact"
      dir={i18n.language === "he" ? "rtl" : "ltr"}
      data-language={i18n.language}
      lang={i18n.language}
    >
      {/* קו חום בתחתית */}
      <div className="brown-line" />

      <main className="contact-main">
        <h1 className="main-heading">{t("contactTitle")}</h1>

        <div className="intro-text">
          <p className="intro-line-1">{t("contactQuestions")}</p>
          <p className="intro-line-2">{t("contactIntro")}</p>
        </div>

        {/* שורה אחת: לוגו | טופס | אייקונים */}
        <div className="bottom-row" role="group" aria-label="contact strip">
          {/* לוגו (מימין) */}
          {/* <div className="logo-container" aria-hidden="true">
            <Logo />
          </div> */}

          {/* טופס במרכז */}
          <form className="email-form" onSubmit={handleSubmit} noValidate>
            <div className="form-container">
              {/* שורה ראשונה - שם ומייל */}
              <div className="name-email-row">
                {/* שדה שם */}
                <input
                  className="name-input"
                  type="text"
                  placeholder={t("namePlaceholder") || "שם מלא"}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  aria-label={t("namePlaceholder") || "שם מלא"}
                  required
                />

                {/* שדה מייל */}
                <div className="email-field-container">
                  <input
                    className={`email-input ${emailError ? "error" : ""}`}
                    type="email"
                    placeholder={t("emailPlaceholder")}
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    aria-label={t("emailPlaceholder")}
                    required
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? "email-error" : undefined}
                  />
                  {emailError && (
                    <div
                      className="email-error-message"
                      role="alert"
                      aria-live="polite"
                      id="email-error"
                    >
                      <span aria-hidden="true">⚠️ </span>
                      {emailError}
                    </div>
                  )}
                </div>
              </div>

              {/* שורה שנייה - הודעה */}
              <div className="message-row">
                <textarea
                  className="message-input"
                  placeholder={
                    t("messagePlaceholder") || "כתוב את הודעתך כאן..."
                  }
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  aria-label={
                    t("messagePlaceholder") || "כתוב את הודעתך כאן..."
                  }
                  required
                  rows={4}
                />
              </div>

              {/* תיבת סימון להסכמה */}
              <div className="consent-checkbox">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={consentGiven}
                    onChange={(e) => setConsentGiven(e.target.checked)}
                    required
                  />
                  <span className="checkbox-text">
                    {t("consentCheckbox")}
                    <Link to="/privacy-policy" className="privacy-link-contact">
                      {t("privacyPolicy")}
                    </Link>{" "}
                    {t("consentCheckboxDescription")}
                  </span>
                </label>
              </div>

              {/* כפתור שליחה */}
              <PrimaryButton
                type="submit"
                disabled={
                  submitting ||
                  !consentGiven ||
                  !validateEmail(email) ||
                  emailError !== ""
                }
              >
                {submitting ? t("sending") : t("sendButton")}
              </PrimaryButton>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default Contact;
